import { useEffect, useState } from 'react';
import {
  getPipeline,
  listClaims,
  listCommissions,
  type ClaimRow,
  type CommissionRow,
} from '../api';
import { useAsync } from '../lib/async';
import { FamilyFilter, NO_FAMILY, byFamily, serverFamily } from '../lib/family';
import { Pager, ScanNote, useCursorList } from '../lib/paging';
import {
  Empty,
  Id,
  Link,
  LocalFilterNote,
  Pill,
  Result,
  StatusPill,
  Toolbar,
  ago,
  when,
} from '../lib/ui';
import { replace } from '../lib/route';

/**
 * Every commission, episodes and peeks together — which is the whole point.
 *
 * A peek (`kind: "preview"`) is `POST /commissions/peek`, handed straight to a
 * background worker — a storage-queue message on this deployment, see
 * shared/workers.py — and never an orchestrated run. Eight of them exist in
 * production. A pipeline screen that counted only runs would have reported the
 * system completely idle while eight commissions sat in it, so peeks are listed
 * here beside episodes and labelled rather than filtered out.
 *
 * This is the DATABASE view: what exists and whose it is. The Executions screen
 * is the ORCHESTRATOR view: where something got to and why it stopped. They
 * disagree in both directions — one run has no commission row, and eight
 * commissions have no run — so each screen says which source it is showing
 * rather than blending them into a number that is true of neither.
 */

const STATUSES = [
  '',
  'generating',
  'awaiting_approval',
  'published',
  'failed',
  'previewing',
  'preview_ready',
];

export function Pipeline({
  initialStatus,
  initialFamily,
  initialKind,
}: {
  initialStatus?: string;
  initialFamily?: string;
  initialKind?: string;
}) {
  const [status, setStatus] = useState(initialStatus ?? '');
  const [family, setFamily] = useState(initialFamily ?? '');
  const [kind, setKind] = useState(initialKind ?? '');
  const [claims, setClaims] = useState(false);

  // Mirror the filters into the URL so "look at these" is a link. replace()
  // rather than push(): a filter that changes as you click would otherwise put
  // an entry in the back stack per dropdown change, and the back button would
  // walk through them instead of leaving the screen.
  useEffect(() => {
    replace({
      name: 'pipeline',
      ...(status ? { status } : {}),
      ...(family ? { familyId: family } : {}),
      ...(kind ? { kind } : {}),
    });
  }, [status, family, kind]);

  return (
    <>
      <Summary />

      <Toolbar>
        <span className="group">
          <label htmlFor="status">Status</label>
          <select id="status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((value) => (
              <option key={value || 'all'} value={value}>
                {value || 'any status'}
              </option>
            ))}
          </select>
        </span>
        <span className="group">
          <label htmlFor="kind">Kind</label>
          <select id="kind" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">episodes and peeks</option>
            <option value="episode">episodes only</option>
            <option value="preview">peeks only</option>
          </select>
        </span>
        <FamilyFilter value={family} onChange={setFamily} />
        <span className="spacer" />
        <button className={claims ? 'on' : ''} onClick={() => setClaims(!claims)}>
          {claims ? 'showing claim rows' : 'show claim rows'}
        </button>
      </Toolbar>

      {claims ? <Claims /> : <Rows status={status} family={family} kind={kind} />}
    </>
  );
}

/**
 * The three numbers from `/admin/pipeline`, above the table they explain.
 *
 * `unavailableDetail` is not decoration here. This is the screen an operator
 * opens during an incident, and before the Azure port it greeted them with a
 * red box reading "Could not load the pipeline summary. Nothing has been ruled
 * out — this is a failed call" and a Try again button that could never work.
 * The commissions table below it was live and correct the whole time. Saying
 * which half of the screen is blind, and why, is the difference between a
 * console you can work from and one you stop trusting.
 */
function Summary() {
  const { result, reload } = useAsync(() => getPipeline(), []);
  return (
    <Result
      of={result}
      what="the pipeline summary"
      retry={reload}
      // The server's own sentence is printed directly above this one, and it
      // says "this deployment runs the pipeline on Durable Functions, which has
      // no state machine and no execution ARNs". This half of the same notice
      // used to answer that with "the Step Functions half", so the box an
      // operator opens during an incident contradicted itself in two adjacent
      // sentences — on the one screen whose whole job is saying which source is
      // blind. Naming the lane rather than the vendor is what keeps them agreed.
      unavailableDetail={
        <>
          The commission table below is a database read and is unaffected — it is
          showing you real rows. What is missing is the orchestrator half: how
          many runs are in flight, and where they got to.{' '}
          {/* The server's sentence directly above this one tells the operator
              to use GET /admin/pipeline/sweep. It is a real, deployed route and
              the console had no way to reach it, so four screens gave the same
              instruction and none of them could follow it. It is wired on the
              Overview now, and this is the link. */}
          <Link to={{ name: 'dashboard' }}>
            The sweep it names is on the Overview
          </Link>
          , under “Needs a human” — that is the one check on this lane that can
          say which families are holding credits for a film nothing is making.
        </>
      }
    >
      {(summary) => (
        <div className="toolbar">
          <span>
            <b>{summary.running ?? '?'}</b> execution
            {summary.running === 1 ? '' : 's'} running
          </span>
          <span className="muted">·</span>
          <span>
            <b>{summary.commissions?.generating ?? '?'}</b> episodes generating
          </span>
          <span className="muted">·</span>
          <span>
            <b>{summary.commissions?.previewing ?? '?'}</b> peeks previewing
          </span>
          <span className="muted">·</span>
          <span>
            <b>{summary.commissions?.creditsHeld ?? '?'}</b> credits held
          </span>
          <span className="spacer" />
          {summary.executionsError && (
            <span className="err small">
              The orchestrator would not answer — the execution count is
              unknown, not zero. {summary.executionsError}
            </span>
          )}
          {summary.commissions?.truncated && (
            <span className="warn small">
              the open-commission scan was truncated; these counts are a floor
            </span>
          )}
        </div>
      )}
    </Result>
  );
}

function Rows({ status, family, kind }: { status: string; family: string; kind: string }) {
  const list = useCursorList<CommissionRow>(
    (cursor) =>
      listCommissions({
        ...(status ? { status } : {}),
        ...(kind ? { kind } : {}),
        ...(serverFamily(family) ? { family: serverFamily(family) } : {}),
        ...(cursor ? { next: cursor } : {}),
      }),
    [status, kind, serverFamily(family) ?? ''],
  );

  return (
    <Result of={list.result} what="commissions" retry={list.reload}>
      {(page) => {
        // The sentinel has no server-side equivalent, so "rows with no family"
        // is the one filter applied here — and it is the filter that finds
        // `veo-test-2`, the row no index returns.
        const rows = family === NO_FAMILY ? byFamily(page.rows, family) : page.rows;
        return (
          <>
            <Toolbar>
              <Pager list={list} what="commissions" shown={rows.length} />
            </Toolbar>
            <LocalFilterNote
              active={family === NO_FAMILY}
              what="commissions"
              why="No endpoint has a query for 'rows with no familyId'."
            />
            {!rows.length ? (
              <Empty
                what="commissions"
                filtered={Boolean(status || kind || family)}
              />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Family</th>
                    <th>Request</th>
                    <th>Kind</th>
                    <th>Status</th>
                    <th className="num">Credits</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.commissionId} className={row.orphaned ? 'flagged' : ''}>
                      <td className="muted nowrap" title={when(row.createdAt)}>
                        {row.createdAt ? ago(row.createdAt) : <span className="warn">no date</span>}
                      </td>
                      <td>
                        {row.familyId ? (
                          <Link to={{ name: 'families', familyId: row.familyId }}>
                            {row.familyName || <Id value={row.familyId} />}
                          </Link>
                        ) : (
                          <Pill tone="bad" title="no familyId: absent from every byFamily index, so invisible to the family's own list too">
                            no family
                          </Pill>
                        )}
                      </td>
                      <td>
                        <span className="clamp" title={row.request}>
                          {row.request || <span className="muted">—</span>}
                        </span>
                      </td>
                      <td>
                        {row.kind === 'preview' ? (
                          <Pill title="a free peek — handed straight to a background worker, never an orchestrated run, so it never appears on the Executions screen">
                            peek
                          </Pill>
                        ) : (
                          <span className="muted">episode</span>
                        )}
                      </td>
                      <td>
                        <StatusPill status={row.status} />
                        {row.failureReason && (
                          <div className="muted small clamp" title={row.failureReason}>
                            {row.failureReason}
                          </div>
                        )}
                      </td>
                      <td className="num">
                        {row.creditsWaived
                          ? <span className="muted" title="unlimited family: no credits moved, real money did">{row.creditsWaived} waived</span>
                          : (row.creditsHeld ?? '')}
                      </td>
                      <td>
                        <Link to={{ name: 'commission', commissionId: row.commissionId }}>
                          open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <ScanNote listing={page} />
          </>
        );
      }}
    </Result>
  );
}

/**
 * The idempotency claim rows, which are normally hidden and should be seen once.
 *
 * `commissions.py` writes a 24-hour `ttl` on these and the table has TTL
 * DISABLED, so nothing sweeps them — they are permanent. `ttlOverdueDays` is how
 * long ago each row asked to be deleted and was not. Half of
 * `showling-characters` is this.
 */
function Claims() {
  const list = useCursorList<ClaimRow>(
    (cursor) => listClaims('commissions', cursor ? { next: cursor } : {}),
    ['commission-claims'],
  );
  return (
    <Result of={list.result} what="claim rows" retry={list.reload}>
      {(page) => (
        <>
          <div className="notice notice-warn">
            <b>Idempotency claim rows — permanent by accident.</b>
            <span className="muted small">
              These carry a `ttl` attribute, but TTL is disabled on
              showling-commissions, so nothing ever deletes them. They have no
              family, no request and no status; they are not commissions.
            </span>
          </div>
          <Toolbar>
            <Pager list={list} what="claim rows" />
          </Toolbar>
          {!page.rows.length ? (
            <Empty what="claim rows" />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Family</th>
                  <th>Written</th>
                  <th>Asked to expire</th>
                  <th>Overdue</th>
                </tr>
              </thead>
              <tbody>
                {page.rows.map((row) => (
                  <tr key={row.commissionId ?? row.characterId}>
                    <td>
                      <Id value={row.commissionId ?? row.characterId} chars={26} />
                    </td>
                    <td>
                      <Id value={row.familyId} />
                    </td>
                    <td className="muted">{when(row.at)}</td>
                    <td className="muted">{when(row.ttlAt)}</td>
                    <td className={row.ttlExpired ? 'neg' : 'muted'}>
                      {row.ttlOverdueDays ? `${row.ttlOverdueDays} days` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <ScanNote listing={page} />
        </>
      )}
    </Result>
  );
}
